import React, { useState, useEffect, useCallback, useRef } from 'react';
import ReactFlow, {
  Node,
  Edge,
  useNodesState,
  useEdgesState,
  Controls,
  Background,
  ReactFlowProvider,
  NodeTypes,
  Handle,
  Position,
  MarkerType,
  useReactFlow,
} from 'reactflow';
import { 
  forceSimulation, 
  forceLink, 
  forceManyBody, 
  forceCenter, 
  forceCollide 
} from 'd3-force';
import 'reactflow/dist/style.css';
import { grammarService } from '../../../services/grammarService';

interface GrammarGraphProps {
  onError: (error: string | null) => void;
}

interface GraphData {
  nodes: Array<{
    id: string;
    label: string;
    category: string;
    cefr_introduction: string;
    cefr_mastery: string;
    complexity_score: number;
    description: string;
    important_footnotes: string;
  }>;
  edges: Array<{
    id: string;
    source: string;
    target: string;
    type: string;
    label: string;
  }>;
  totalNodes: number;
  totalEdges: number;
}

interface SelectedNode {
  id: string;
  label: string;
  category: string;
  cefr_introduction: string;
  cefr_mastery: string;
  complexity_score: number;
  description: string;
  important_footnotes: string;
}

// D3 Force-Directed Layout - The Proven Solution!
const applyD3ForceLayout = (
  nodes: any[], 
  edges: any[], 
  containerWidth: number, 
  containerHeight: number,
  onTick: (nodes: any[]) => void,
  onEnd?: () => void // Add callback for when simulation ends
): (() => void) => {
  
  console.log(`🚀 Starting D3 force simulation: ${nodes.length} nodes, ${edges.length} edges`);
  
  // Prepare data for D3
  const d3Nodes = nodes.map(node => ({
    ...node,
    x: containerWidth / 2 + (Math.random() - 0.5) * 100,
    y: containerHeight / 2 + (Math.random() - 0.5) * 100,
  }));
  
  // Create a set of all node IDs for validation
  const nodeIds = new Set(d3Nodes.map(node => node.id));
  
  // Validate and filter edges to only include valid connections
  const validEdges = edges.filter(edge => {
    const sourceExists = nodeIds.has(edge.source);
    const targetExists = nodeIds.has(edge.target);
    return sourceExists && targetExists;
  });
  
  const invalidCount = edges.length - validEdges.length;
  if (invalidCount > 0) {
    console.warn(`⚠️ Filtered out ${invalidCount} invalid edges (${validEdges.length}/${edges.length} valid)`);
  }
  
  const d3Links = validEdges.map(edge => ({
    source: edge.source,
    target: edge.target,
  }));
  
  // Create D3 force simulation with better performance settings
  const simulation = forceSimulation(d3Nodes)
    .force('link', forceLink(d3Links)
      .id((d: any) => d.id)
      .distance(120)
      .strength(0.6)
    )
    .force('charge', forceManyBody()
      .strength(-400)
      .distanceMax(400)
    )
    .force('center', forceCenter(containerWidth / 2, containerHeight / 2))
    .force('collision', forceCollide()
      .radius(40)
      .strength(0.8)
    )
    .alphaDecay(0.05) // Much faster convergence
    .velocityDecay(0.6) // Higher dampening
    .alphaMin(0.03); // Stop sooner
  
  // Force stop after 2 seconds regardless
  const forceStopTimeout = setTimeout(() => {
    console.log('🛑 Force stopping simulation after 2 seconds');
    simulation.stop();
    if (onEnd) onEnd();
  }, 2000);
  
  // Update React state on each tick with throttling
  let tickCount = 0;
  let lastUpdateTime = 0;
  const updateThrottle = 16; // ~60fps max
  
  simulation.on('tick', () => {
    tickCount++;
    const now = Date.now();
    
    // Throttle updates for better performance
    if (now - lastUpdateTime < updateThrottle) return;
    lastUpdateTime = now;
    
    // Update original nodes array with D3 positions - NO CLAMPING!
    d3Nodes.forEach(d3Node => {
      const originalNode = nodes.find(n => n.id === d3Node.id);
      if (originalNode) {
        originalNode.position = {
          x: d3Node.x || 0,
          y: d3Node.y || 0
        };
      }
    });
    
    // Call update callback
    onTick([...nodes]);
    
    // Log progress less frequently
    if (tickCount % 100 === 0) {
      console.log(`⚡ D3 tick ${tickCount}, alpha: ${simulation.alpha().toFixed(3)}`);
    }
  });
  
  simulation.on('end', () => {
    console.log(`✅ D3 simulation completed after ${tickCount} ticks`);
    if (onEnd) onEnd();
  });
  
  // Return object with simulation controls
  return () => {
    simulation.stop();
    clearTimeout(forceStopTimeout);
  };
};

// Circular node component
const CircularNode = ({ data, selected }: { data: any; selected: boolean }) => {
  const getCategoryColor = (category: string) => {
    const colors = [
      '#ef4444', '#f97316', '#eab308', '#22c55e', '#06b6d4', 
      '#3b82f6', '#8b5cf6', '#ec4899', '#f59e0b', '#10b981',
    ];
    const hash = category.split('').reduce((a, b) => {
      a = ((a << 5) - a) + b.charCodeAt(0);
      return a & a;
    }, 0);
    return colors[Math.abs(hash) % colors.length];
  };

  const isHighlighted = data.isHighlighted;
  const isSelected = data.isSelected;
  const opacity = data.showHighlighting && !data.isHighlighted ? 0.3 : 1;

  return (
    <div 
      className={`relative ${selected ? 'z-10' : ''}`}
      style={{ width: 60, height: 60, opacity }}
    >
      {/* Invisible handles at the center for edge connections */}
      <Handle 
        type="target" 
        position={Position.Left}
        style={{ 
          background: 'transparent', 
          border: 'none',
          width: '1px',
          height: '1px',
          left: '50%',
          top: '50%',
          transform: 'translate(-50%, -50%)'
        }}
      />
      <Handle 
        type="source" 
        position={Position.Right}
        style={{ 
          background: 'transparent', 
          border: 'none',
          width: '1px',
          height: '1px',
          left: '50%',
          top: '50%',
          transform: 'translate(-50%, -50%)'
        }}
      />
      
      <div
        className={`w-full h-full rounded-full border-4 flex items-center justify-center shadow-lg transition-all duration-200 ${
          isSelected ? 'border-blue-600 shadow-xl scale-110' : 
          isHighlighted ? 'border-blue-400 shadow-lg scale-105' : 'border-white'
        }`}
        style={{
          backgroundColor: getCategoryColor(data.category),
          cursor: 'pointer'
        }}
      >
        {data.showLabels && (
          <div 
            className="absolute top-16 left-1/2 transform -translate-x-1/2 bg-white px-2 py-1 rounded shadow text-xs font-medium whitespace-nowrap z-20"
            style={{ maxWidth: '150px' }}
          >
            {data.label}
          </div>
        )}
      </div>
    </div>
  );
};

const nodeTypes: NodeTypes = {
  circular: CircularNode,
};

// Inner component with D3 force simulation
const GrammarGraphInner: React.FC<GrammarGraphProps> = ({ onError }) => {
  const [loading, setLoading] = useState<boolean>(true);
  const [graphData, setGraphData] = useState<GraphData | null>(null);
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [selectedEdgeType, setSelectedEdgeType] = useState<string>('prerequisite');
  const [showLabels, setShowLabels] = useState<boolean>(false);
  const [selectedNode, setSelectedNode] = useState<SelectedNode | null>(null);
  const [highlightedNodes, setHighlightedNodes] = useState<Set<string>>(new Set());
  const [highlightedEdges, setHighlightedEdges] = useState<Set<string>>(new Set());
  const containerRef = useRef<HTMLDivElement>(null);
  const simulationCleanupRef = useRef<(() => void) | null>(null);
  const { fitView } = useReactFlow();

  useEffect(() => {
    fetchGraphData();
  }, []);

  useEffect(() => {
    if (graphData) {
      applyFilters();
    }
  }, [graphData, selectedEdgeType, showLabels]);

  // Cleanup simulation on unmount
  useEffect(() => {
    return () => {
      if (simulationCleanupRef.current) {
        simulationCleanupRef.current();
      }
    };
  }, []);

  // Update highlighting without re-running simulation
  useEffect(() => {
    if (!graphData) return;

    setNodes(currentNodes => 
      currentNodes.map(node => ({
        ...node,
        data: {
          ...node.data,
          isHighlighted: highlightedNodes.has(node.id),
          isSelected: selectedNode?.id === node.id,
          showHighlighting: highlightedNodes.size > 0,
        }
      }))
    );

    const filteredEdges = graphData.edges.filter(edge => edge.type === selectedEdgeType);
    setEdges(currentEdges => 
      currentEdges.map(edge => ({
        ...edge,
        style: {
          ...edge.style,
          stroke: highlightedEdges.has(edge.id) ? '#3b82f6' : '#6b7280',
          strokeWidth: highlightedEdges.has(edge.id) ? 3 : 2,
          opacity: highlightedNodes.size > 0 ? (highlightedEdges.has(edge.id) ? 1 : 0.2) : 1,
        },
        markerEnd: {
          type: MarkerType.Arrow,
          color: highlightedEdges.has(edge.id) ? '#3b82f6' : '#6b7280',
        },
      }))
    );
  }, [highlightedNodes, highlightedEdges, selectedNode, graphData, selectedEdgeType, setNodes, setEdges]);

  const fetchGraphData = async () => {
    try {
      setLoading(true);
      onError(null);
      const data = await grammarService.getGraphData();
      setGraphData(data);
    } catch (error) {
      console.error('Error fetching graph data:', error);
      onError(error instanceof Error ? error.message : 'Failed to fetch graph data');
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = useCallback(() => {
    if (!graphData) return;

    // Cleanup previous simulation
    if (simulationCleanupRef.current) {
      simulationCleanupRef.current();
    }

    // Get actual container dimensions
    const containerWidth = containerRef.current?.clientWidth || 400;
    const containerHeight = containerRef.current?.clientHeight || 600;

    // Filter edges by selected type
    const filteredEdges = graphData.edges.filter(edge => edge.type === selectedEdgeType);
    
    // Get only nodes that are connected by the selected edge type
    const connectedNodeIds = new Set();
    filteredEdges.forEach(edge => {
      connectedNodeIds.add(edge.source);
      connectedNodeIds.add(edge.target);
    });

    const filteredNodes = graphData.nodes.filter(node => connectedNodeIds.has(node.id));

    // Convert to React Flow format
    let flowNodes: Node[] = filteredNodes.map(node => ({
      id: node.id,
      type: 'circular',
      position: { x: containerWidth / 2, y: containerHeight / 2 }, // D3 will set proper positions
      data: {
        label: node.label,
        category: node.category,
        cefr_introduction: node.cefr_introduction,
        cefr_mastery: node.cefr_mastery,
        complexity_score: node.complexity_score,
        description: node.description,
        important_footnotes: node.important_footnotes,
        showLabels: showLabels,
        isHighlighted: false, // Will be updated by highlighting effect
        isSelected: false, // Will be updated by highlighting effect  
        showHighlighting: false, // Will be updated by highlighting effect
      },
      draggable: true, // Enable dragging
    }));

    const flowEdges: Edge[] = filteredEdges.map(edge => ({
      id: edge.id,
      source: edge.source,
      target: edge.target,
      type: 'straight',
      animated: false,
      style: {
        stroke: '#6b7280', // Will be updated by highlighting effect
        strokeWidth: 2, // Will be updated by highlighting effect
      },
      markerEnd: {
        type: MarkerType.Arrow,
        color: '#6b7280', // Will be updated by highlighting effect
      },
    }));

    // Set initial nodes and edges
    setNodes(flowNodes);
    setEdges(flowEdges);

    // Start D3 force simulation
    if (flowNodes.length > 0) {
      simulationCleanupRef.current = applyD3ForceLayout(
        flowNodes,
        filteredEdges,
        containerWidth,
        containerHeight,
        (updatedNodes) => {
          setNodes(updatedNodes);
        },
        () => {
          // Smooth zoom animation to fit all nodes
          setTimeout(() => {
            fitView({
              duration: 800, // 800ms smooth animation
              padding: 0.1,  // 10% padding around the graph
              includeHiddenNodes: false,
            });
          }, 100); // Small delay to ensure nodes are rendered
        }
      );
    }

  }, [graphData, selectedEdgeType, showLabels, setNodes, setEdges]);

  const onNodeClick = useCallback((event: React.MouseEvent, node: Node) => {
    const nodeData = graphData?.nodes.find(n => n.id === node.id);
    if (nodeData) {
      setSelectedNode(nodeData);
      
      // Find connected nodes and edges
      const connectedNodeIds = new Set<string>();
      const connectedEdgeIds = new Set<string>();
      
      if (graphData) {
        // Get edges for the current filter
        const filteredEdges = graphData.edges.filter(edge => edge.type === selectedEdgeType);
        
        filteredEdges.forEach(edge => {
          if (edge.source === node.id) {
            connectedNodeIds.add(edge.target);
            connectedEdgeIds.add(edge.id);
          } else if (edge.target === node.id) {
            connectedNodeIds.add(edge.source);
            connectedEdgeIds.add(edge.id);
          }
        });
        
        // Also highlight the selected node itself
        connectedNodeIds.add(node.id);
      }
      
      setHighlightedNodes(connectedNodeIds);
      setHighlightedEdges(connectedEdgeIds);
      
      console.log(`🎯 Selected ${node.id}, highlighting ${connectedNodeIds.size} nodes and ${connectedEdgeIds.size} edges`);
    }
  }, [graphData, selectedEdgeType]);

  const categories = graphData ? Array.from(new Set(graphData.nodes.map(node => node.category))).sort() : [];
  const relationTypes = [
    { value: 'prerequisite', label: 'Prerequisites', description: 'Features that must be learned before this one' },
    { value: 'enables', label: 'Enables', description: 'Features that this one helps enable' },
    { value: 'combines_with', label: 'Combines With', description: 'Features that work well together' },
    { value: 'related', label: 'Related', description: 'Similar or related features' },
  ];

  const getCategoryColor = (category: string) => {
    const colors = [
      '#ef4444', '#f97316', '#eab308', '#22c55e', '#06b6d4', 
      '#3b82f6', '#8b5cf6', '#ec4899', '#f59e0b', '#10b981',
    ];
    const hash = category.split('').reduce((a, b) => {
      a = ((a << 5) - a) + b.charCodeAt(0);
      return a & a;
    }, 0);
    return colors[Math.abs(hash) % colors.length];
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-talaera-blue"></div>
      </div>
    );
  }

  return (
    <div className="h-full flex">
      {/* Main Graph Area */}
      <div className="flex-1 flex flex-col">
        {/* Controls */}
        <div className="bg-white p-4 border-b space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold">Grammar Feature Relationships</h3>
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                <label className="text-sm font-medium">Edge Type:</label>
                <select
                  value={selectedEdgeType}
                  onChange={(e) => setSelectedEdgeType(e.target.value)}
                  className="px-3 py-1 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                >
                  {relationTypes.map(type => (
                    <option key={type.value} value={type.value}>{type.label}</option>
                  ))}
                </select>
              </div>
              <div className="flex items-center space-x-2">
                <label className="text-sm font-medium">Show Labels:</label>
                <input
                  type="checkbox"
                  checked={showLabels}
                  onChange={(e) => setShowLabels(e.target.checked)}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
              </div>
            </div>
          </div>
          
          <div className="text-sm text-gray-600">
            <strong>Current Relationship:</strong> {relationTypes.find(t => t.value === selectedEdgeType)?.description}
            <span className="ml-4"><strong>Nodes:</strong> {nodes.length}</span>
            <span className="ml-4"><strong>Connections:</strong> {edges.length}</span>
            <span className="ml-4 text-green-600"><strong>⚡ Engine:</strong> D3-Force (industry standard)</span>
          </div>
        </div>

        {/* Graph */}
        <div ref={containerRef} className="flex-1" style={{ minHeight: '600px' }}>
          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onNodeClick={onNodeClick}
            onPaneClick={() => {
              // Clear highlighting when clicking empty space
              setHighlightedNodes(new Set());
              setHighlightedEdges(new Set());
              setSelectedNode(null);
            }}
            nodeTypes={nodeTypes}
            nodesDraggable={true}
            nodesConnectable={false}
            elementsSelectable={true}
            fitView={false}
            attributionPosition="bottom-left"
            defaultViewport={{ x: 0, y: 0, zoom: 0.8 }}
            minZoom={0.1}
            maxZoom={2}
          >
            <Background />
            <Controls />
          </ReactFlow>
        </div>

        {/* Category Legend */}
        <div className="bg-white p-4 border-t">
          <h4 className="font-medium mb-3">Categories</h4>
          <div className="flex flex-wrap gap-3">
            {categories.map(category => (
              <div key={category} className="flex items-center space-x-2">
                <div 
                  className="w-4 h-4 rounded-full border border-white shadow"
                  style={{ backgroundColor: getCategoryColor(category) }}
                />
                <span className="text-sm">{category}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Details Panel */}
      <div className="w-80 bg-gray-50 border-l flex flex-col">
        <div className="p-4 border-b bg-white">
          <h3 className="text-lg font-semibold">Feature Details</h3>
        </div>
        
        {selectedNode ? (
          <div className="flex-1 p-4 space-y-4 overflow-y-auto">
            <div>
              <div className="flex items-center space-x-3 mb-3">
                <div 
                  className="w-6 h-6 rounded-full border-2 border-white shadow"
                  style={{ backgroundColor: getCategoryColor(selectedNode.category) }}
                />
                <div>
                  <h4 className="font-semibold text-lg">{selectedNode.label}</h4>
                  <p className="text-sm text-gray-600">{selectedNode.category}</p>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="bg-white p-3 rounded-lg">
                <div className="text-xs text-gray-500 uppercase tracking-wider">CEFR Introduction</div>
                <div className="text-lg font-semibold">{selectedNode.cefr_introduction}</div>
              </div>
              <div className="bg-white p-3 rounded-lg">
                <div className="text-xs text-gray-500 uppercase tracking-wider">CEFR Mastery</div>
                <div className="text-lg font-semibold">{selectedNode.cefr_mastery}</div>
              </div>
            </div>

            <div className="bg-white p-3 rounded-lg">
              <div className="text-xs text-gray-500 uppercase tracking-wider mb-1">Complexity Score</div>
              <div className="text-2xl font-bold">{selectedNode.complexity_score}</div>
            </div>

            {selectedNode.description && (
              <div className="bg-white p-3 rounded-lg">
                <div className="text-xs text-gray-500 uppercase tracking-wider mb-2">Description</div>
                <p className="text-sm leading-relaxed">{selectedNode.description}</p>
              </div>
            )}

            {selectedNode.important_footnotes && (
              <div className="bg-yellow-50 p-3 rounded-lg border border-yellow-200">
                <div className="text-xs text-yellow-700 uppercase tracking-wider mb-2">Important Notes</div>
                <p className="text-sm leading-relaxed text-yellow-800">{selectedNode.important_footnotes}</p>
              </div>
            )}
          </div>
        ) : (
          <div className="flex-1 flex items-center justify-center text-gray-500">
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gray-200 flex items-center justify-center">
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <p>Click a node to view details</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const GrammarGraph: React.FC<GrammarGraphProps> = ({ onError }) => {
  return (
    <ReactFlowProvider>
      <GrammarGraphInner onError={onError} />
    </ReactFlowProvider>
  );
};

export default GrammarGraph; 